/** Why the advisory pre-check says this page may not use the site key. */
export type SiteNotAuthorizedReason = 'origin_not_allowed' | 'unknown_site_key';

export type SiteStatus =
  | { status: 'authorized' }
  | { status: 'not_authorized'; reason: SiteNotAuthorizedReason }
  | { status: 'unavailable' };

/** What `FieldsController`/`CardFieldHandle` need from the pre-check. */
export interface SiteStatusGate {
  /** The outcome if already settled, else `undefined`. */
  peek(): SiteStatus | undefined;
  whenSettled(): Promise<SiteStatus>;
  /** One `console.warn` per gate (i.e. per `Menta` instance). */
  warnUnavailableOnce(): void;
  /** One actionable `console.error` per gate (i.e. per `Menta` instance). */
  errorNotAuthorizedOnce(reason: SiteNotAuthorizedReason): void;
}

const TIMEOUT_MS = 3000;

function parseReason(value: unknown): SiteNotAuthorizedReason | undefined {
  return value === 'origin_not_allowed' || value === 'unknown_site_key' ? value : undefined;
}

async function interpret(response: Response): Promise<SiteStatus> {
  const body: unknown = await response.json().catch(() => null);
  const status = typeof body === 'object' && body !== null ? (body as { status?: unknown }).status : undefined;

  if (response.status === 200 && status === 'authorized') return { status: 'authorized' };

  if ((response.status === 403 || response.status === 404) && status === 'not_authorized') {
    const reason = parseReason((body as { reason?: unknown }).reason);
    if (reason) return { status: 'not_authorized', reason };
  }
  return { status: 'unavailable' };
}

/**
 * ADVISORY pre-check against the Worker's `GET /site-status`. It is not a
 * security control: `frame-ancestors` and the coordinator handshake remain
 * the enforcement. Starts once on construction, never throws, and always
 * settles (any failure, including the 3s timeout, is `unavailable`).
 * Deliberately a CORS simple request: no custom headers, no credentials.
 */
export class SiteStatusCheck implements SiteStatusGate {
  private outcome: SiteStatus | undefined;
  private readonly settled: Promise<SiteStatus>;
  private warnedUnavailable = false;
  private erroredNotAuthorized = false;

  constructor(origin: string, siteKey: string) {
    this.settled = this.run(origin, siteKey).then((outcome) => {
      this.outcome = outcome;
      return outcome;
    });
  }

  peek(): SiteStatus | undefined {
    return this.outcome;
  }

  whenSettled(): Promise<SiteStatus> {
    return this.settled;
  }

  warnUnavailableOnce(): void {
    if (this.warnedUnavailable) return;
    this.warnedUnavailable = true;
    console.warn(
      '[menta] Could not verify the site key before mounting (site-status pre-check unavailable). ' +
        'Mounting the card fields anyway; if the key or this page origin is not authorized, ' +
        'they will fail with "handshake_failed" after ~8s. Check that your CSP connect-src allows the secure-fields origin.',
    );
  }

  errorNotAuthorizedOnce(reason: SiteNotAuthorizedReason): void {
    if (this.erroredNotAuthorized) return;
    this.erroredNotAuthorized = true;
    const hint =
      reason === 'unknown_site_key'
        ? 'the site key does not exist: check the key passed to new Menta(...)'
        : 'this page origin is not in the site\'s allowed origins: add it in the site settings';
    console.error(`[menta] site_not_authorized (${reason}): card fields were not mounted; ${hint}.`);
  }

  private async run(origin: string, siteKey: string): Promise<SiteStatus> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(`${origin}/site-status?site_key=${encodeURIComponent(siteKey)}`, {
        method: 'GET',
        credentials: 'omit',
        mode: 'cors',
        cache: 'no-store',
        signal: controller.signal,
      });
      return await interpret(response);
    } catch {
      return { status: 'unavailable' };
    } finally {
      clearTimeout(timer);
    }
  }
}
